import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { UserType } from '../../interfaces/userType.enum';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useDashboardAccess } from '../dashboard/useDashboardAccess';
import './Sidebar.css';
import { List, ListItem, ListItemText, Collapse, IconButton } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

interface SidebarProps {
    collapsed: boolean;
    onToggle: () => void;
}

interface MenuGroupLink {
    path: string;
    label: string;
    icon: React.ReactNode;
}

interface MenuGroupProps {
    label: string;
    icon: React.ReactNode;
    links: MenuGroupLink[];
    collapsed: boolean;
    open: boolean;
    onToggle: () => void;
    pathname: string;
}

/** A collapsible sidebar section. Renders nothing when the user can see none of its links. */
const MenuGroup = ({ label, icon, links, collapsed, open, onToggle, pathname }: MenuGroupProps) => {
    if (links.length === 0) {
        return null;
    }
    return (
        <List component="nav" sx={{ padding: 0 }}>
            <ListItem
                component="div"
                onClick={collapsed ? undefined : onToggle}
                title={collapsed ? label : ''}
                sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    padding: collapsed ? '10px 0' : '10px 20px',
                    justifyContent: collapsed ? 'center' : 'flex-start',
                    cursor: collapsed ? 'default' : 'pointer',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                }}
            >
                <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>{icon}</span>
                {!collapsed && <ListItemText primary={label} />}
                {!collapsed && (open ? <ExpandLess /> : <ExpandMore />)}
            </ListItem>
            <Collapse in={!collapsed && open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {links.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className={`menuItem submenu ${pathname === link.path ? 'active' : ''}`}
                        >
                            <span className="icon">{link.icon}</span>
                            {link.label}
                        </Link>
                    ))}
                </List>
            </Collapse>
        </List>
    );
};

const Sidebar = ({ collapsed, onToggle }: SidebarProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { t } = useTranslation('shared');

    const leaveRoutes = ['/leave-applications', '/all-leave-applications', '/leave-type', '/leave-policy', '/user-leave-allocations', '/all-user-leave-balances'];
    const attendanceRoutes = ['/attendance', '/holidays'];
    const peopleRoutes = ['/users', '/employee-documents', '/departments'];
    const settingsRoutes = ['/institution-settings', '/device-users', '/device-api-keys', '/billing'];
    const accountingRoutes = ['/accounting'];
    const payrollRoutes = ['/payroll/'];
    const taxRoutes = ['/tax/'];
    const taskRoutes = ['/tasks', '/admin/tasks'];
    const noticeRoutes = ['/notices', '/admin/notices'];
    const performanceRoutes = ['/appraisals'];

    const [leaveMenuOpen, setLeaveMenuOpen] = useState(() => leaveRoutes.some(r => location.pathname.startsWith(r)));
    const [peopleMenuOpen, setPeopleMenuOpen] = useState(() => peopleRoutes.some(r => location.pathname.startsWith(r)));
    const [settingsMenuOpen, setSettingsMenuOpen] = useState(() => settingsRoutes.some(r => location.pathname.startsWith(r)));
    const [accountingMenuOpen, setAccountingMenuOpen] = useState(() => accountingRoutes.some(r => location.pathname.startsWith(r)));
    const [attendanceMenuOpen, setAttendanceMenuOpen] = useState(() => attendanceRoutes.some(r => location.pathname.startsWith(r)));
    const [payrollMenuOpen, setPayrollMenuOpen] = useState(() => payrollRoutes.some(r => location.pathname.startsWith(r)));
    const [taxMenuOpen, setTaxMenuOpen] = useState(() => taxRoutes.some(r => location.pathname.startsWith(r)));
    const [taskMenuOpen, setTaskMenuOpen] = useState(() => taskRoutes.some(r => location.pathname.startsWith(r)));
    const [noticeMenuOpen, setNoticeMenuOpen] = useState(() => noticeRoutes.some(r => location.pathname.startsWith(r)));
    const [performanceMenuOpen, setPerformanceMenuOpen] = useState(() => performanceRoutes.some(r => location.pathname.startsWith(r)));
    const [shiftsMenuOpen, setShiftsMenuOpen] = useState(() => location.pathname.startsWith('/shifts'));
    const [dashboardMenuOpen, setDashboardMenuOpen] = useState(() => location.pathname === '/' || location.pathname.startsWith('/dashboard'));

    // Convert user roles to array for checking
    const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];

    // Check if user has specific role
    const hasRole = (role: UserType) => userRoles.includes(role);

    // Check permissions
    const isSystemAdmin = hasRole(UserType.SystemAdmin);
    const isInstitutionAdmin = hasRole(UserType.InstitutionAdmin);
    const isInstitutionManager = hasRole(UserType.InstitutionManager);
    const isAccountant = hasRole(UserType.Accountant);
    const isSaaSOperator = hasRole(UserType.SaaSOperator);

    // Board roles get an expandable "Dashboard" menu (Overview + module deep-pages);
    // everyone else keeps the flat Dashboard link. The module sub-links are gated by the
    // user's role-registry modules intersected with the institution's enabled set, so a
    // restricted user only sees their modules.
    const isDashboardBoardRole = isInstitutionAdmin || isInstitutionManager || isAccountant;
    const { visibleModules, enabledModules, ready: modulesReady } = useDashboardAccess();

    // Fine-grained permission checks (manual overrides via usePermission)
    const canAccessDashboard = usePermission(PERMISSIONS.MENU_DASHBOARD, true);
    const canAccessAttendance = usePermission(PERMISSIONS.MENU_ATTENDANCE, true);
    const canAccessLeave = usePermission(PERMISSIONS.MENU_LEAVE, true, [
        PERMISSIONS.SUBMENU_LEAVE_ALL_APPLICATIONS,
        PERMISSIONS.SUBMENU_LEAVE_BALANCES,
        PERMISSIONS.SUBMENU_LEAVE_TYPES,
        PERMISSIONS.SUBMENU_LEAVE_POLICY,
        PERMISSIONS.SUBMENU_LEAVE_ALLOCATIONS,
    ]);
    const canAccessHolidays = usePermission(PERMISSIONS.MENU_HOLIDAY, isInstitutionAdmin || isInstitutionManager);
    const canAccessUsers = usePermission(PERMISSIONS.MENU_USERS, isInstitutionAdmin);
    const canAccessDevices = usePermission(PERMISSIONS.MENU_DEVICES, isInstitutionAdmin);
    const canAccessApiKeys = usePermission(PERMISSIONS.MENU_API_KEYS, isInstitutionAdmin);
    const isDepartmentAdmin = user?.isDepartmentAdmin === true;
    const canAccessDepartments = isInstitutionAdmin || isDepartmentAdmin;
    const canAccessPayroll = usePermission(PERMISSIONS.MENU_PAYROLL, isInstitutionAdmin || isSystemAdmin);
    const canAccessTax = usePermission(PERMISSIONS.MENU_TAX, isInstitutionAdmin || isAccountant);
    // Accounting is a paid add-on: the module must be on for the institution, and the user needs the menu.
    // enabledModules, not visibleModules: the latter only holds modules with dashboard widgets.
    // Wait for modulesReady: until the enabled-modules call returns, every module reads as enabled.
    const canAccessAccounting = usePermission(PERMISSIONS.MENU_ACCOUNTING, isInstitutionAdmin || isAccountant)
        && modulesReady && enabledModules.has('accounting');
    const canAccessNotices = usePermission(PERMISSIONS.MENU_NOTICES, true);
    const canManageNotices = usePermission(PERMISSIONS.ACTION_MANAGE_NOTICES, isInstitutionAdmin);
    const canAccessTasks = usePermission(PERMISSIONS.MENU_TASKS, true);
    const canAssignTasks = usePermission(PERMISSIONS.ACTION_ASSIGN_TASK, isInstitutionAdmin);
    // Appraisals are tenant-only: operators have no access to a tenant's appraisals.
    const isOperator = isSystemAdmin || isSaaSOperator;
    const canManageAppraisals = usePermission(PERMISSIONS.MENU_APPRAISAL, isInstitutionAdmin) && !isOperator;

    // Shift permissions
    const canAccessShifts = usePermission(PERMISSIONS.MENU_SHIFTS, isInstitutionAdmin || isInstitutionManager, [
        PERMISSIONS.SUBMENU_SHIFT_LIST,
        PERMISSIONS.SUBMENU_SHIFT_ASSIGNMENTS,
        PERMISSIONS.SUBMENU_SHIFT_ROTATION_PATTERNS,
        PERMISSIONS.SUBMENU_SHIFT_ROSTER,
        PERMISSIONS.SUBMENU_SHIFT_ORPHANS,
    ]);
    const canAccessShiftList = usePermission(PERMISSIONS.SUBMENU_SHIFT_LIST, isInstitutionAdmin || isInstitutionManager);
    const canAccessShiftAssignments = usePermission(PERMISSIONS.SUBMENU_SHIFT_ASSIGNMENTS, isInstitutionAdmin);
    const canAccessShiftRotation = usePermission(PERMISSIONS.SUBMENU_SHIFT_ROTATION_PATTERNS, isInstitutionAdmin);
    const canAccessShiftRoster = usePermission(PERMISSIONS.SUBMENU_SHIFT_ROSTER, isInstitutionAdmin);
    const canAccessShiftOrphans = usePermission(PERMISSIONS.SUBMENU_SHIFT_ORPHANS, isInstitutionAdmin);

    // Leave sub-menu permissions
    const canSeeLeaveAllApplications = usePermission(PERMISSIONS.SUBMENU_LEAVE_ALL_APPLICATIONS, isInstitutionAdmin);
    const canSeeLeaveBalances        = usePermission(PERMISSIONS.SUBMENU_LEAVE_BALANCES,         isInstitutionAdmin);
    const canSeeLeaveTypes           = usePermission(PERMISSIONS.SUBMENU_LEAVE_TYPES,            isInstitutionAdmin);
    const canSeeLeavePolicy          = usePermission(PERMISSIONS.SUBMENU_LEAVE_POLICY,           isInstitutionAdmin);
    const canSeeLeaveAllocations     = usePermission(PERMISSIONS.SUBMENU_LEAVE_ALLOCATIONS,      isInstitutionAdmin);

    const handleLeaveMenuClick = () => {
        setLeaveMenuOpen(!leaveMenuOpen);
    };

    const handleAttendanceMenuClick = () => {
        setAttendanceMenuOpen(!attendanceMenuOpen);
    };

    const handlePayrollMenuClick = () => {
        setPayrollMenuOpen(!payrollMenuOpen);
    };

    const handleTaxMenuClick = () => {
        setTaxMenuOpen(!taxMenuOpen);
    };

    const hasAttendanceGroup = canAccessAttendance && isInstitutionAdmin;

    // Filter menu items based on permissions
    const menuItems = [
        ...(canAccessDashboard && !isDashboardBoardRole ? [{ path: '/', label: t('sidebar.links.dashboard'), icon: '📊' }] : []),

        // Institutions menu - only for System Admin (no permission override needed)
        ...(isSystemAdmin ? [
            { path: '/institutions', label: t('sidebar.links.institutions'), icon: '🏢' },
            { path: '/all-institution-users', label: t('sidebar.links.allInstitutionUsers'), icon: '👥' }
        ] : []),

        // SaaS Operator console — premium dashboard provisioning across institutions
        ...(isSaaSOperator || isSystemAdmin ? [
            { path: '/saas/premium-provisioning', label: t('sidebar.links.premiumProvisioning'), icon: '💎' },
        ] : []),

        // SaaS Operator console — subscription billing across institutions
        ...(isSaaSOperator || isSystemAdmin ? [
            { path: '/operator/billing', label: t('sidebar.links.subscriptionBilling'), icon: <ReceiptLongIcon fontSize="small" /> },
        ] : []),

        // Attendance link for non-admin users only (admins get a submenu instead)
        ...(canAccessAttendance && !isInstitutionAdmin ? [
            { path: '/attendance', label: t('sidebar.links.attendance'), icon: '📋' },
        ] : []),
        // Holidays sit inside the Attendance group for admins; everyone else keeps the flat link
        ...(canAccessHolidays && !hasAttendanceGroup ? [{ path: '/holidays', label: t('sidebar.links.holidays'), icon: '🎉' }] : []),
];

    const peopleLinks: MenuGroupLink[] = [
        ...(canAccessUsers ? [
            { path: '/users', label: t('sidebar.links.users'), icon: '👥' },
            { path: '/employee-documents', label: t('sidebar.links.employeeProfile'), icon: '📄' },
        ] : []),
        ...(canAccessDepartments ? [{ path: '/departments', label: t('sidebar.links.departments'), icon: '🏛️' }] : []),
    ];

    const accountingLinks: MenuGroupLink[] = canAccessAccounting ? [
        { path: '/accounting', label: t('sidebar.links.overview'), icon: '📒' },
        { path: '/accounting/entries', label: t('sidebar.links.journalEntries'), icon: '🧾' },
        { path: '/accounting/accounts', label: t('sidebar.links.chartOfAccounts'), icon: '🗂️' },
        { path: '/accounting/reports', label: t('sidebar.links.reports'), icon: '📈' },
        { path: '/accounting/opening-balances', label: t('sidebar.links.openingBalances'), icon: '🏁' },
        { path: '/accounting/closing', label: t('sidebar.links.closing'), icon: '🔒' },
    ] : [];

    const settingsLinks: MenuGroupLink[] = [
        // Institution Admin can edit a limited set of their own institution's settings
        ...(isInstitutionAdmin && !isSystemAdmin ? [
            { path: '/institution-settings', label: t('sidebar.links.institutionSettings'), icon: '⚙️' },
        ] : []),
        ...(canAccessDevices ? [{ path: '/device-users', label: t('sidebar.links.deviceUsers'), icon: '📱' }] : []),
        ...(canAccessApiKeys ? [{ path: '/device-api-keys', label: t('sidebar.links.apiKeys'), icon: '🔑' }] : []),
        // Billing — Institution Admin only (subscription invoices + bKash checkout)
        ...(isInstitutionAdmin ? [
            { path: '/billing', label: t('sidebar.links.billing'), icon: <ReceiptLongIcon fontSize="small" /> },
        ] : []),
    ];

    // "Reviews to write" shows for everyone; the page has an empty state for people who review nobody.
    const performanceLinks: MenuGroupLink[] = isOperator ? [] : [
        { path: '/appraisals/mine', label: t('sidebar.links.myAppraisals'), icon: '⭐' },
        { path: '/appraisals/to-write', label: t('sidebar.links.reviewsToWrite'), icon: '📝' },
        ...(canManageAppraisals ? [{ path: '/appraisals/cycles', label: t('sidebar.links.appraisalCycles'), icon: '🗓️' }] : []),
    ];

    const handleDashboardMenuClick = () => setDashboardMenuOpen(!dashboardMenuOpen);
    const handleTaskMenuClick = () => setTaskMenuOpen(!taskMenuOpen);
    const handleNoticeMenuClick = () => setNoticeMenuOpen(!noticeMenuOpen);
    const handleShiftsMenuClick = () => setShiftsMenuOpen(!shiftsMenuOpen);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="sidebar" style={{ width: collapsed ? '60px' : '250px' }}>
            <div className="logo">
                <div className="logo-inner">
                    {!collapsed && <h2>Kervolt</h2>}
                    <IconButton onClick={onToggle} className="sidebar-toggle" size="small">
                        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                    </IconButton>
                </div>
            </div>
            <nav className="nav">
                {canAccessDashboard && isDashboardBoardRole && (
                <div className="dashboard-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleDashboardMenuClick}
                            title={collapsed ? t('sidebar.groups.dashboard') : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>📊</span>
                            {!collapsed && <ListItemText primary={t('sidebar.groups.dashboard')} />}
                            {!collapsed && (dashboardMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && dashboardMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/"
                                    className={`menuItem submenu ${location.pathname === '/' ? 'active' : ''}`}
                                >
                                    <span className="icon">🏠</span>
                                    {t('sidebar.links.overview')}
                                </Link>
                                {visibleModules.has('attendance') && (
                                    <Link
                                        to="/dashboard/attendance"
                                        className={`menuItem submenu ${location.pathname === '/dashboard/attendance' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📈</span>
                                        {t('sidebar.links.dashboardAttendance')}
                                    </Link>
                                )}
                                {visibleModules.has('leave') && (
                                    <Link
                                        to="/dashboard/leave"
                                        className={`menuItem submenu ${location.pathname === '/dashboard/leave' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📊</span>
                                        {t('sidebar.links.dashboardLeave')}
                                    </Link>
                                )}
                                {visibleModules.has('people') && (
                                    <Link
                                        to="/dashboard/people"
                                        className={`menuItem submenu ${location.pathname === '/dashboard/people' ? 'active' : ''}`}
                                    >
                                        <span className="icon">🧑‍🤝‍🧑</span>
                                        {t('sidebar.links.dashboardPeople')}
                                    </Link>
                                )}
                                {visibleModules.has('payroll') && (
                                    <Link
                                        to="/dashboard/payroll"
                                        className={`menuItem submenu ${location.pathname === '/dashboard/payroll' ? 'active' : ''}`}
                                    >
                                        <span className="icon">💵</span>
                                        {t('sidebar.links.dashboardPayroll')}
                                    </Link>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}
                {menuItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`menuItem ${location.pathname === item.path ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`}
                        title={collapsed ? item.label : ''}
                    >
                        <span className="icon">{item.icon}</span>
                        {!collapsed && item.label}
                    </Link>
                ))}

                <div className="people-management">
                    <MenuGroup
                        label={t('sidebar.groups.people')} icon="👥" links={peopleLinks} collapsed={collapsed}
                        open={peopleMenuOpen} onToggle={() => setPeopleMenuOpen(!peopleMenuOpen)} pathname={location.pathname}
                    />
                </div>

                {hasAttendanceGroup && (
                <div className="attendance-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleAttendanceMenuClick}
                            title={collapsed ? t('sidebar.groups.attendance') : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>📋</span>
                            {!collapsed && <ListItemText primary={t('sidebar.groups.attendance')} />}
                            {!collapsed && (attendanceMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && attendanceMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/attendance"
                                    className={`menuItem submenu ${location.pathname === '/attendance' ? 'active' : ''}`}
                                >
                                    <span className="icon">📄</span>
                                    {t('sidebar.links.myAttendance')}
                                </Link>
                                <Link
                                    to="/attendance/employee"
                                    className={`menuItem submenu ${location.pathname === '/attendance/employee' ? 'active' : ''}`}
                                >
                                    <span className="icon">👤</span>
                                    {t('sidebar.links.employeeAttendance')}
                                </Link>
                                <Link
                                    to="/attendance/today"
                                    className={`menuItem submenu ${location.pathname === '/attendance/today' ? 'active' : ''}`}
                                >
                                    <span className="icon">📅</span>
                                    {t('sidebar.links.todaysAttendance')}
                                </Link>
                                {canAccessHolidays && (
                                    <Link
                                        to="/holidays"
                                        className={`menuItem submenu ${location.pathname === '/holidays' ? 'active' : ''}`}
                                    >
                                        <span className="icon">🎉</span>
                                        {t('sidebar.links.holidays')}
                                    </Link>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}

                {canAccessLeave && <div className="leave-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleLeaveMenuClick}
                            title={collapsed ? t('sidebar.groups.leave') : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>📝</span>
                            {!collapsed && <ListItemText primary={t('sidebar.groups.leave')} />}
                            {!collapsed && (leaveMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && leaveMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/leave-applications"
                                    className={`menuItem submenu ${location.pathname === '/leave-applications' ? 'active' : ''}`}
                                >
                                    <span className="icon">📄</span>
                                    {t('sidebar.links.myApplications')}
                                </Link>
                                {canSeeLeaveAllApplications && (
                                    <Link
                                        to="/all-leave-applications"
                                        className={`menuItem submenu ${location.pathname === '/all-leave-applications' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📋</span>
                                        {t('sidebar.links.allApplications')}
                                    </Link>
                                )}
                                {canSeeLeaveTypes && (
                                    <Link
                                        to="/leave-type"
                                        className={`menuItem submenu ${location.pathname === '/leave-type' ? 'active' : ''}`}
                                    >
                                        <span className="icon">🏷️</span>
                                        {t('sidebar.links.leaveTypes')}
                                    </Link>
                                )}
                                {canSeeLeavePolicy && (
                                    <Link
                                        to="/leave-policy"
                                        className={`menuItem submenu ${location.pathname === '/leave-policy' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📜</span>
                                        {t('sidebar.links.leavePolicy')}
                                    </Link>
                                )}
                                {canSeeLeaveAllocations && (
                                    <Link
                                        to="/user-leave-allocations"
                                        className={`menuItem submenu ${location.pathname === '/user-leave-allocations' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📊</span>
                                        {t('sidebar.links.leaveAllocations')}
                                    </Link>
                                )}
                                {canSeeLeaveBalances && (
                                    <Link
                                        to="/all-user-leave-balances"
                                        className={`menuItem submenu ${location.pathname === '/all-user-leave-balances' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📈</span>
                                        {t('sidebar.links.leaveBalances')}
                                    </Link>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>}

                {canAccessShifts && (
                <div className="shifts-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleShiftsMenuClick}
                            title={collapsed ? t('sidebar.groups.shifts') : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>🕐</span>
                            {!collapsed && <ListItemText primary={t('sidebar.groups.shifts')} />}
                            {!collapsed && (shiftsMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && shiftsMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                {canAccessShiftList && (
                                    <Link
                                        to="/shifts"
                                        className={`menuItem submenu ${location.pathname === '/shifts' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📋</span>
                                        {t('sidebar.links.shiftList')}
                                    </Link>
                                )}
                                {canAccessShiftAssignments && (
                                    <Link
                                        to="/shifts/assignments"
                                        className={`menuItem submenu ${location.pathname === '/shifts/assignments' ? 'active' : ''}`}
                                    >
                                        <span className="icon">👤</span>
                                        {t('sidebar.links.assignments')}
                                    </Link>
                                )}
                                {canAccessShiftRotation && (
                                    <Link
                                        to="/shifts/rotation-patterns"
                                        className={`menuItem submenu ${location.pathname === '/shifts/rotation-patterns' ? 'active' : ''}`}
                                    >
                                        <span className="icon">🔁</span>
                                        {t('sidebar.links.rotationPatterns')}
                                    </Link>
                                )}
                                {canAccessShiftRoster && (
                                    <Link
                                        to="/shifts/roster"
                                        className={`menuItem submenu ${location.pathname === '/shifts/roster' ? 'active' : ''}`}
                                    >
                                        <span className="icon">📅</span>
                                        {t('sidebar.links.roster')}
                                    </Link>
                                )}
                                {canAccessShiftOrphans && (
                                    <Link
                                        to="/shifts/orphan-punches"
                                        className={`menuItem submenu ${location.pathname === '/shifts/orphan-punches' ? 'active' : ''}`}
                                    >
                                        <span className="icon">👻</span>
                                        {t('sidebar.links.orphanPunches')}
                                    </Link>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}

                {canAccessPayroll && (
                <div className="payroll-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handlePayrollMenuClick}
                            title={collapsed ? t('sidebar.groups.payroll') : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>💵</span>
                            {!collapsed && <ListItemText primary={t('sidebar.groups.payroll')} />}
                            {!collapsed && (payrollMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && payrollMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/payroll/salary-components"
                                    className={`menuItem submenu ${location.pathname === '/payroll/salary-components' ? 'active' : ''}`}
                                >
                                    <span className="icon">📋</span>
                                    {t('sidebar.links.salaryComponents')}
                                </Link>
                                <Link
                                    to="/payroll/employee-salaries"
                                    className={`menuItem submenu ${location.pathname === '/payroll/employee-salaries' ? 'active' : ''}`}
                                >
                                    <span className="icon">👤</span>
                                    {t('sidebar.links.employeeSalaries')}
                                </Link>
                                <Link
                                    to="/payroll/settings"
                                    className={`menuItem submenu ${location.pathname === '/payroll/settings' ? 'active' : ''}`}
                                >
                                    <span className="icon">⚙️</span>
                                    {t('sidebar.links.payrollSettings')}
                                </Link>
                                <Link
                                    to="/payroll/run"
                                    className={`menuItem submenu ${location.pathname === '/payroll/run' ? 'active' : ''}`}
                                >
                                    <span className="icon">💰</span>
                                    {t('sidebar.links.monthlyPayroll')}
                                </Link>
                                <Link
                                    to="/payroll/reconciliation"
                                    className={`menuItem submenu ${location.pathname === '/payroll/reconciliation' ? 'active' : ''}`}
                                >
                                    <span className="icon">🧾</span>
                                    {t('sidebar.links.reconciliation')}
                                </Link>
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}

                {canAccessTax && (
                <div className="tax-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleTaxMenuClick}
                            title={collapsed ? t('sidebar.groups.tax') : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>🧾</span>
                            {!collapsed && <ListItemText primary={t('sidebar.groups.tax')} />}
                            {!collapsed && (taxMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && taxMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/tax/settings"
                                    className={`menuItem submenu ${location.pathname === '/tax/settings' ? 'active' : ''}`}
                                >
                                    <span className="icon">⚙️</span>
                                    {t('sidebar.links.taxSettings')}
                                </Link>
                                <Link
                                    to="/tax/slabs"
                                    className={`menuItem submenu ${location.pathname === '/tax/slabs' ? 'active' : ''}`}
                                >
                                    <span className="icon">📊</span>
                                    {t('sidebar.links.taxSlabs')}
                                </Link>
                                <Link
                                    to="/tax/exemptions"
                                    className={`menuItem submenu ${location.pathname === '/tax/exemptions' ? 'active' : ''}`}
                                >
                                    <span className="icon">🔖</span>
                                    {t('sidebar.links.exemptions')}
                                </Link>
                                <Link
                                    to="/tax/employees"
                                    className={`menuItem submenu ${location.pathname === '/tax/employees' ? 'active' : ''}`}
                                >
                                    <span className="icon">👤</span>
                                    {t('sidebar.links.employeeTax')}
                                </Link>
                                <Link
                                    to="/tax/reports"
                                    className={`menuItem submenu ${location.pathname === '/tax/reports' ? 'active' : ''}`}
                                >
                                    <span className="icon">📄</span>
                                    {t('sidebar.links.taxReports')}
                                </Link>
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}

                {canAccessNotices && (
                <div className="notice-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleNoticeMenuClick}
                            title={collapsed ? t('sidebar.groups.notices') : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>📢</span>
                            {!collapsed && <ListItemText primary={t('sidebar.groups.notices')} />}
                            {!collapsed && (noticeMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && noticeMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/notices"
                                    className={`menuItem submenu ${location.pathname === '/notices' ? 'active' : ''}`}
                                >
                                    <span className="icon">📄</span>
                                    {t('sidebar.links.notices')}
                                </Link>
                                {canManageNotices && (
                                    <Link
                                        to="/admin/notices"
                                        className={`menuItem submenu ${location.pathname === '/admin/notices' ? 'active' : ''}`}
                                    >
                                        <span className="icon">🗂️</span>
                                        {t('sidebar.links.manageNotices')}
                                    </Link>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}

                {canAccessTasks && (
                <div className="task-management">
                    <List component="nav" sx={{ padding: 0 }}>
                        <ListItem
                            component="div"
                            onClick={collapsed ? undefined : handleTaskMenuClick}
                            title={collapsed ? t('sidebar.groups.tasks') : ''}
                            sx={{
                                color: 'inherit',
                                textDecoration: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                padding: collapsed ? '10px 0' : '10px 20px',
                                justifyContent: collapsed ? 'center' : 'flex-start',
                                cursor: collapsed ? 'default' : 'pointer',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                }
                            }}
                        >
                            <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>✅</span>
                            {!collapsed && <ListItemText primary={t('sidebar.groups.tasks')} />}
                            {!collapsed && (taskMenuOpen ? <ExpandLess /> : <ExpandMore />)}
                        </ListItem>
                        <Collapse in={!collapsed && taskMenuOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <Link
                                    to="/tasks"
                                    className={`menuItem submenu ${location.pathname === '/tasks' ? 'active' : ''}`}
                                >
                                    <span className="icon">📄</span>
                                    {t('sidebar.links.myTasks')}
                                </Link>
                                {canAssignTasks && (
                                    <>
                                        <Link
                                            to="/tasks/assigned-by-me"
                                            className={`menuItem submenu ${location.pathname === '/tasks/assigned-by-me' ? 'active' : ''}`}
                                        >
                                            <span className="icon">📤</span>
                                            {t('sidebar.links.tasksIAssigned')}
                                        </Link>
                                        <Link
                                            to="/admin/tasks"
                                            className={`menuItem submenu ${location.pathname === '/admin/tasks' ? 'active' : ''}`}
                                        >
                                            <span className="icon">🗂️</span>
                                            {t('sidebar.links.allTasks')}
                                        </Link>
                                    </>
                                )}
                            </List>
                        </Collapse>
                    </List>
                </div>
                )}
                <div className="performance-management">
                    <MenuGroup
                        label={t('sidebar.groups.performance')} icon="📈" links={performanceLinks} collapsed={collapsed}
                        open={performanceMenuOpen} onToggle={() => setPerformanceMenuOpen(!performanceMenuOpen)} pathname={location.pathname}
                    />
                </div>
                <div className="accounting-management">
                    <MenuGroup
                        label={t('sidebar.groups.accounting')} icon="📒" links={accountingLinks} collapsed={collapsed}
                        open={accountingMenuOpen} onToggle={() => setAccountingMenuOpen(!accountingMenuOpen)} pathname={location.pathname}
                    />
                </div>
                <div className="settings-management">
                    <MenuGroup
                        label={t('sidebar.groups.settings')} icon="⚙️" links={settingsLinks} collapsed={collapsed}
                        open={settingsMenuOpen} onToggle={() => setSettingsMenuOpen(!settingsMenuOpen)} pathname={location.pathname}
                    />
                </div>
            </nav>
            <div className="logout-section">
                <button onClick={handleLogout} className={`logout-button ${collapsed ? 'collapsed' : ''}`} title={collapsed ? t('sidebar.logout') : ''}>
                    <span className="icon" style={{ marginRight: collapsed ? 0 : undefined }}>🚪</span>
                    {!collapsed && t('sidebar.logout')}
                </button>
            </div>
        </div>
    );
};

export default Sidebar;
